import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Storefront, SignOut, CheckCircle, Camera, Clock, Truck } from '@phosphor-icons/react';
import CameraCapture from '../../components/CameraCapture';
import VersionPill from '../../components/VersionPill';

const STATUS_META = {
  pending: { label: 'Awaiting DC', color: 'text-[#FF8C00]', bg: 'bg-[#FF8C00]/10' },
  allocated: { label: 'Allocated', color: 'text-[#002FA7]', bg: 'bg-[#002FA7]/10' },
  accepted: { label: 'Accepted', color: 'text-[#002FA7]', bg: 'bg-[#002FA7]/10' },
  trip_started: { label: 'In Transit', color: 'text-[#002FA7]', bg: 'bg-[#002FA7]/10' },
  pickup_reached: { label: 'At Pickup', color: 'text-[#002FA7]', bg: 'bg-[#002FA7]/10' },
  pickup_done: { label: 'Picked Up', color: 'text-[#00C853]', bg: 'bg-[#00C853]/10' },
  drop_reached: { label: 'At Drop', color: 'text-[#00C853]', bg: 'bg-[#00C853]/10' },
  completed: { label: 'Completed', color: 'text-[#00C853]', bg: 'bg-[#00C853]/10' },
};

function fmtTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function TripCard({ trip, onMarkReady }) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const statusMeta = STATUS_META[trip.status] || { label: trip.status, color: 'text-[#666]', bg: 'bg-[#F3F3F3]' };
  const dcReady = !!trip.dc_ready;

  const handleCapture = async ({ photo, gps }) => {
    setCameraOpen(false);
    setSaving(true);
    try {
      await api.put(`/pickup/trips/${trip.id}/mark-ready`, { dc_photo: photo, dc_photo_gps: gps || null });
      onMarkReady();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to mark goods ready');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid={`pickup-trip-${trip.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-black tracking-tighter">#{trip.id}</p>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${statusMeta.bg} ${statusMeta.color}`}>{statusMeta.label}</span>
            {trip.priority && trip.priority !== 'others' && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#FF3333]/10 text-[#FF3333]">{trip.priority}</span>
            )}
          </div>
          <p className="text-xs text-[#666] mt-1">Booked {fmtTime(trip.created_at)}</p>
        </div>
        {dcReady && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 bg-[#00C853]/10 text-[#00C853]" data-testid={`pickup-trip-${trip.id}-dc-ready`}>
            <CheckCircle size={12} weight="bold" /> DC Ready
          </span>
        )}
      </div>

      {/* Prominent vehicle-allocated banner — the pickup user writes this
          number on the DC before marking goods ready. */}
      {trip.vehicle_number ? (
        <div className="bg-[#002FA7] text-white p-3" data-testid={`pickup-trip-${trip.id}-vehicle-banner`}>
          <p className="text-[10px] uppercase tracking-widest text-white/70">Vehicle Allocated · Write on DC</p>
          <p className="text-2xl font-black tracking-tight font-mono mt-0.5">{trip.vehicle_number}</p>
          {trip.pilot_name && (
            <p className="text-xs text-white/80 mt-1">
              Pilot: {trip.pilot_name}
              {trip.pilot_mobile && (
                <>
                  {' · '}
                  <a href={`tel:${trip.pilot_mobile}`} className="underline hover:no-underline">{trip.pilot_mobile}</a>
                </>
              )}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-[#FF8C00]/10 border border-[#FF8C00]/20 p-3" data-testid={`pickup-trip-${trip.id}-awaiting-vehicle`}>
          <p className="text-xs text-[#FF8C00] font-medium">⏳ Awaiting vehicle allocation — you'll get the vehicle number shortly on this dashboard and via WhatsApp.</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <span className="text-[#666]">Pickup</span><span className="font-medium truncate">{trip.pickup_location || '-'}</span>
        <span className="text-[#666]">Drop</span><span className="font-medium truncate">{trip.drop_location || '-'}</span>
        <span className="text-[#666]">Item</span><span className="font-medium truncate">{trip.item || '-'}</span>
        <span className="text-[#666]">Qty</span><span className="font-medium">{trip.qty || 0} {trip.uom || ''}</span>
      </div>

      {dcReady && trip.dc_photo && (
        <a href={trip.dc_photo} target="_blank" rel="noopener noreferrer" className="block">
          <img src={trip.dc_photo} alt="DC" className="w-full max-h-48 object-contain bg-[#F3F3F3] border border-[#E5E5E5]" data-testid={`pickup-trip-${trip.id}-dc-photo`} />
        </a>
      )}

      {!dcReady && (
        <Button
          onClick={() => setCameraOpen(true)}
          disabled={saving}
          className="w-full bg-[#00C853] text-white hover:bg-[#00C853]/90 hover:text-white rounded-none h-12 text-sm uppercase tracking-widest font-bold"
          data-testid={`pickup-trip-${trip.id}-mark-ready-btn`}
        >
          <Camera size={18} className="mr-2" />
          {saving ? 'Saving…' : 'Attach DC & Mark Goods Ready'}
        </Button>
      )}

      {cameraOpen && (
        <CameraCapture
          label={`DC PHOTO · TRIP ${trip.id}`}
          onClose={() => setCameraOpen(false)}
          onCapture={handleCapture}
        />
      )}
    </div>
  );
}

export default function PickupDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [me, tr] = await Promise.all([
        api.get('/pickup/me'),
        api.get('/pickup/trips'),
      ]);
      setProfile(me.data);
      setTrips(tr.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const pending = trips.filter(t => !t.dc_ready);
  const ready = trips.filter(t => t.dc_ready);

  return (
    <div className="flex flex-col min-h-screen bg-[#F7F7F7]" data-testid="pickup-layout">
      <header className="bg-[#0A0A0A] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <Storefront size={22} weight="bold" className="text-[#8E44AD]" />
          <div>
            <p className="text-sm font-bold">{profile?.name || user?.name || 'Pickup'}</p>
            <p className="text-[10px] uppercase tracking-widest text-white/60">{profile?.pickup_supplier_name || '—'}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="text-white/70 hover:text-white flex items-center gap-2" data-testid="pickup-logout">
          {/* Phase 76u — version pill */}
          <VersionPill className="!bg-white/10 !text-white/70" />
          <SignOut size={20} />
        </button>
      </header>

      <div className="flex-1 p-4 space-y-4 max-w-3xl mx-auto w-full">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#FF8C00]/10 border border-[#FF8C00]/20 p-3 text-center" data-testid="pickup-stat-pending">
            <Clock size={18} className="mx-auto text-[#FF8C00]" weight="bold" />
            <p className="text-2xl font-black text-[#FF8C00] mt-1">{pending.length}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Awaiting DC</p>
          </div>
          <div className="bg-[#00C853]/10 border border-[#00C853]/20 p-3 text-center" data-testid="pickup-stat-ready">
            <CheckCircle size={18} className="mx-auto text-[#00C853]" weight="bold" />
            <p className="text-2xl font-black text-[#00C853] mt-1">{ready.length}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">DC Ready</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] p-3 text-center" data-testid="pickup-stat-total">
            <Truck size={18} className="mx-auto text-[#002FA7]" weight="bold" />
            <p className="text-2xl font-black text-[#002FA7] mt-1">{trips.length}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#666]">Total Today</p>
          </div>
        </div>

        <section>
          <h2 className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A] mb-2">Pending — Attach DC</h2>
          {pending.length === 0 ? (
            <p className="text-sm text-[#666] py-6 text-center bg-white border border-[#E5E5E5]">
              {loading ? 'Loading…' : 'No trips awaiting DC. You will see new trips here within 30s of booking.'}
            </p>
          ) : (
            <div className="space-y-3" data-testid="pickup-pending-list">
              {pending.map(t => <TripCard key={t.id} trip={t} onMarkReady={fetchAll} />)}
            </div>
          )}
        </section>

        {ready.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-widest font-bold text-[#0A0A0A] mb-2">DC Ready · In Progress</h2>
            <div className="space-y-3" data-testid="pickup-ready-list">
              {ready.map(t => <TripCard key={t.id} trip={t} onMarkReady={fetchAll} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
